/**
 * Commission Tier System - Database-First Architecture
 *
 * ⚠️ IMPORTANT: This file provides LEGACY FALLBACK utilities for client-side calculations.
 *
 * **Production Commission Calculations:**
 * - All production calculations happen SERVER-SIDE using PostgreSQL functions
 * - Tiers are stored in `commission_tier_config` table with temporal versioning
 * - Brand-specific tiers allow different commission structures per brand
 * - Use `get_commission_rate(brand, roas, date)` database function for calculations
 *
 * **This File's Purpose:**
 * - Fallback calculations when database is unavailable
 * - Client-side validation and preview calculations
 * - Unit testing without database dependency
 * - Functions accept custom tier arrays from database
 *
 * **Database-First Flow:**
 * 1. Admin schedules tier changes via `/admin/edit-commissions` UI
 * 2. Tiers stored in `commission_tier_config` with effective dates
 * 3. Database functions aggregate by brand, then apply brand-specific tiers
 * 4. Frontend loads tiers from database via `brandService.getCommissionTiers()`
 * 5. This file's functions can use custom tiers: `calculateTieredCommission(roas, spend, tiers)`
 *
 * **Architecture Notes:**
 * - Tiers are per-brand: holistic_hercules, aphrodite, etc.
 * - Temporal versioning: tiers have effective_from_date and effective_until_date
 * - Future-only edits: cannot modify tiers for past dates (protects historical data)
 * - Per-brand aggregation: calculate ROAS by brand first, then apply brand tiers
 *
 * @see db_functions/affiliate/get_commission_rate.sql
 * @see db_functions/affiliate/set_commission_tiers.sql
 * @see supabase/migrations/20251014090000_create_commission_tier_config.sql
 */

// LEGACY: Default tier structure for fallback calculations
// ⚠️ Production uses database tiers, NOT these hardcoded values
export const COMMISSION_TIERS = [
  { minROAS: 0.8, percentage: 0.038, label: "3.8%" }, // 0.8 to < 1.2
  { minROAS: 1.2, percentage: 0.045, label: "4.5%" }, // 1.2 to < 1.4
  { minROAS: 1.4, percentage: 0.048, label: "4.8%" }, // 1.4 to < 1.5
  { minROAS: 1.5, percentage: 0.05, label: "5.0%" }, // 1.5 to < 1.6
  { minROAS: 1.6, percentage: 0.054, label: "5.4%" }, // 1.6 to < 1.7
  { minROAS: 1.7, percentage: 0.058, label: "5.8%" }, // 1.7 to < 1.8
  { minROAS: 1.8, percentage: 0.064, label: "6.4%" }, // 1.8 to < 1.9
  { minROAS: 1.9, percentage: 0.066, label: "6.6%" }, // 1.9 to < 2.1
  { minROAS: 2.1, percentage: 0.07, label: "7.0%" }, // 2.1 to < 2.5
  { minROAS: 2.5, percentage: 0.08, label: "8.0%" }, // 2.5 and above
];

const ensureTiers = (tiers) =>
  Array.isArray(tiers) && tiers.length > 0 ? tiers : COMMISSION_TIERS;

// REFERRAL system constants
export const REFERRAL_COMMISSION_RATE = 0.1; // 10% of referee's commission earnings

/**
 * Calculate commission based on ROAS performance
 *
 * ⚠️ LEGACY FUNCTION: For client-side fallback calculations only.
 * Production calculations use server-side `get_commission_rate(brand, roas, date)`.
 *
 * @param {number} roas - Return on Ad Spend
 * @param {number} adSpend - Total ad spend
 * @param {Array} [tiers=COMMISSION_TIERS] - Custom tier array from database (optional)
 * @returns {object} - Commission details including amount, tier, and breakdown
 *
 * @example
 * // With database-loaded tiers
 * const tiers = await brandService.getCommissionTiers('holistic_hercules');
 * const result = calculateTieredCommission(1.85, 1000, tiers);
 *
 * @example
 * // With default tiers (fallback)
 * const result = calculateTieredCommission(1.85, 1000);
 */
export const calculateTieredCommission = (roas, adSpend, tiers = COMMISSION_TIERS) => {
  const activeTiers = ensureTiers(tiers);

  // Handle edge cases
  if (!roas || roas < 0.8 || !adSpend || adSpend <= 0) {
    return {
      commission: 0,
      percentage: 0,
      tier: null,
      label: "Below minimum ROAS (0.8)",
      breakdown: null,
    };
  }

  // Find the appropriate tier
  // Find the highest tier where ROAS >= minROAS
  let tier = null;
  for (let i = activeTiers.length - 1; i >= 0; i--) {
    if (roas >= activeTiers[i].minROAS) {
      tier = activeTiers[i];
      break;
    }
  }

  if (!tier) {
    return {
      commission: 0,
      percentage: 0,
      tier: null,
      label: "No tier found",
      breakdown: null,
    };
  }

  const commission = adSpend * tier.percentage;

  return {
    commission,
    percentage: tier.percentage,
    tier,
    label: tier.label,
    breakdown: {
      roas: roas.toFixed(2),
      adSpend,
      tier: `${tier.minROAS}+`,
      percentage: tier.label,
      calculation: `$${adSpend.toFixed(2)} × ${tier.label} = $${commission.toFixed(2)}`,
    },
  };
};

/**
 * Calculate total commission for multiple ads with detailed breakdown
 *
 * ⚠️ LEGACY FUNCTION: For client-side fallback calculations only.
 * Production calculations use server-side per-brand aggregation with temporal tiers.
 *
 * @param {Array} ads - Array of ad objects with roas and adSpend
 * @param {Array} [tiers=COMMISSION_TIERS] - Custom tier array from database (optional)
 * @returns {object} - Total commission and detailed breakdown by tier
 *
 * @example
 * // With database-loaded tiers
 * const tiers = await brandService.getCommissionTiers('holistic_hercules');
 * const result = calculateAggregateCommission(ads, tiers);
 */
export const calculateAggregateCommission = (ads, tiers = COMMISSION_TIERS) => {
  const activeTiers = ensureTiers(tiers);
  const tierBreakdown = {};
  let totalCommission = 0;
  const adBreakdowns = [];

  // Initialize tier breakdown
  activeTiers.forEach((tier) => {
    tierBreakdown[tier.label] = {
      count: 0,
      totalSpend: 0,
      totalCommission: 0,
      tier,
    };
  });

  // Process each ad
  ads.forEach((ad) => {
    const result = calculateTieredCommission(ad.roas, ad.adSpend, activeTiers);

    if (result.commission > 0 && result.tier) {
      totalCommission += result.commission;

      // Add to tier breakdown
      const tierLabel = result.tier.label;
      tierBreakdown[tierLabel].count += 1;
      tierBreakdown[tierLabel].totalSpend += ad.adSpend;
      tierBreakdown[tierLabel].totalCommission += result.commission;

      // Add ad breakdown
      adBreakdowns.push({
        adName: ad.adName || ad.name || "Unknown Ad",
        ...result.breakdown,
        commission: result.commission,
      });
    }
  });

  // Filter out empty tiers
  const populatedTiers = Object.entries(tierBreakdown)
    .filter(([_, data]) => data.count > 0)
    .map(([label, data]) => ({ label, ...data }));

  return {
    totalCommission,
    tierBreakdown: populatedTiers,
    adBreakdowns,
    summary: {
      totalAds: ads.length,
      qualifyingAds: adBreakdowns.length,
      averageCommissionRate:
        totalCommission > 0
          ? totalCommission / ads.reduce((sum, ad) => sum + ad.adSpend, 0)
          : 0,
    },
  };
};

/**
 * Get commission tier information for display
 * @param {number} roas - Return on Ad Spend
 * @returns {object} - Tier information
 */
export const getTierInfo = (roas) => {
  const tiers = COMMISSION_TIERS;

  // Find the highest tier where ROAS >= minROAS
  for (let i = tiers.length - 1; i >= 0; i--) {
    if (roas >= tiers[i].minROAS) {
      return tiers[i];
    }
  }

  return null;
};

/**
 * Get all commission tiers for display in UI
 *
 * ⚠️ IMPORTANT: For production UI, load tiers from database:
 * `await brandService.getCommissionTiers(brandKey)`
 *
 * @param {Array} [tiers=COMMISSION_TIERS] - Custom tier array from database (optional)
 * @returns {Array} - All commission tiers
 *
 * @example
 * // Load from database (PREFERRED)
 * const tiers = await brandService.getCommissionTiers('holistic_hercules');
 *
 * @example
 * // Use default fallback tiers
 * const tiers = getAllTiers();
 */
export const getAllTiers = (tiers = COMMISSION_TIERS) => ensureTiers(tiers);

/**
 * Calculate referral commission based on referee's earnings
 * @param {number} refereeCommissionEarnings - Total commission earned by referee
 * @returns {number} - Referral commission amount (10% of referee's commission)
 */
export const calculateReferralCommission = (refereeCommissionEarnings) => {
  if (!refereeCommissionEarnings || refereeCommissionEarnings <= 0) {
    return 0;
  }
  return refereeCommissionEarnings * REFERRAL_COMMISSION_RATE;
};

/**
 * Calculate referee commission earnings for referral calculation
 *
 * ⚠️ LEGACY FUNCTION: For client-side fallback calculations only.
 * Production uses server-side `calculate_weekly_referral_bonuses()` with per-brand aggregation.
 *
 * **Database Function Pattern:**
 * 1. Aggregate spend/revenue by brand for referee
 * 2. Calculate brand-specific ROAS
 * 3. Apply brand-specific temporal tier via `get_commission_rate(brand, roas, date)`
 * 4. Sum commissions across all brands
 *
 * @param {number} totalSpend - Total ad spend by referee
 * @param {number} totalRevenue - Total revenue by referee
 * @param {Array} [tiers=COMMISSION_TIERS] - Custom tier array from database (optional)
 * @returns {object} - Commission details
 */
export const calculateRefereeCommissionForReferral = (
  totalSpend,
  totalRevenue,
  tiers = COMMISSION_TIERS
) => {
  if (!totalSpend || totalSpend <= 0) {
    return { commission: 0, roas: 0 };
  }

  const roas = totalRevenue / totalSpend;
  const commissionResult = calculateTieredCommission(roas, totalSpend, tiers);

  return {
    commission: commissionResult.commission,
    roas,
    tier: commissionResult.tier,
    breakdown: commissionResult.breakdown,
  };
};
